import { useEffect, useRef, useState } from 'react'
import type { Scenario, ScenarioId } from '../scenarios'

/**
 * The compare affordance: one button before, an A/B switch after.
 *
 * Centred, not `self-start`. The controls section is a flex column, so a button
 * that only hugs its content sits hard against the left edge with a wide gap to
 * its right — fine in a desktop sidebar, visibly out of position on a phone,
 * where this is the widest thing on screen after the sliders. It matches the
 * "Share this" button above the fold, which is centred for the same reason.
 *
 * ── Why arrival is a single button ──────────────────────────────────────────
 * Most of the traffic this product is built for lands from a link on a phone and
 * gets one chance: link → move a slider → see the number → share it. Comparison
 * is the second thing anyone wants and the first thing that would make the first
 * thing heavier, so it costs exactly one control until it is asked for, and the
 * screen a first-time visitor sees is unchanged.
 *
 * ── Identity is never colour alone ──────────────────────────────────────────
 * The chips carry the labels, the delta line names the scenario in words, and the
 * chart's tooltip prefixes its numbers with the label. The hue is a fast second
 * signal, never the only one — the same rule the allocation bar follows.
 *
 * ── One root, both states ───────────────────────────────────────────────────
 * The two branches used to return different root elements — a bare `<button>`
 * before, a `<div>` after — so React unmounted the whole subtree on both
 * transitions. Whichever control you had just activated ceased to exist and focus
 * fell to `<body>`, which on a phone closes the keyboard and on a desktop loses
 * your place entirely. The root is now a `<div>` in both states, and the live
 * region lives inside it so it survives the swap and can actually be read.
 *
 * A stable root is necessary and not sufficient: the focused control is itself
 * what disappears, so focus is moved deliberately in the effect below. It is only
 * moved when it would otherwise be lost — see the guard there.
 */

interface ScenarioBarProps {
  scenarios: Scenario[]
  activeId: ScenarioId
  onFocus: (id: ScenarioId) => void
  onCompare: () => void
  onStopComparing: () => void
}

/** Arrow keys, plus Home and End. `radiogroup` is expected to answer all of these. */
const NAV_KEYS = ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End']

export function ScenarioBar({
  scenarios,
  activeId,
  onFocus,
  onCompare,
  onStopComparing,
}: ScenarioBarProps) {
  const comparing = scenarios.length > 1

  const rootRef = useRef<HTMLDivElement | null>(null)
  const compareRef = useRef<HTMLButtonElement | null>(null)
  const chipRefs = useRef(new Map<ScenarioId, HTMLButtonElement | null>())

  /** Empty until something changes, or every page load would announce itself. */
  const [announcement, setAnnouncement] = useState('')
  const wasComparing = useRef(comparing)
  /** Set by our own two controls, so the effect can tell intent from coincidence. */
  const userActedRef = useRef(false)

  const startComparing = () => {
    userActedRef.current = true
    onCompare()
  }
  const stopComparing = () => {
    userActedRef.current = true
    onStopComparing()
  }

  useEffect(() => {
    if (wasComparing.current === comparing) return
    wasComparing.current = comparing

    /* Move focus only when the READER caused this, tracked as intent rather than
       inferred from where focus happens to be.
       An earlier version tested "is focus on <body>" and treated that as "our own
       control just unmounted". It is also the state after Back/Forward, and after
       pasting a two-plan share link into the address bar of an already-open tab:
       same path, so it arrives as a hashchange, the set is adopted, `comparing`
       flips with nobody focused, and the effect would yank focus to a chip and
       scroll it into view. That is the unrequested focus move the old comment
       claimed to prevent — the test was simply the wrong one. */
    const userActed = userActedRef.current
    userActedRef.current = false

    if (userActed) {
      if (comparing) chipRefs.current.get(activeId)?.focus()
      else compareRef.current?.focus()
    }

    /* Announces the STRUCTURAL change only, and deliberately says nothing about
       which plan is selected while the chips exist.

       Two reasons, both found by driving this in a browser. Naming the plan on the
       way in duplicates the chip: focus lands on it and it announces itself as
       "Plan B, selected, radio", so a region saying "Editing Plan B" makes a
       screen reader say it twice. And the text would go stale — arrow-key
       navigation changes the selection without changing `comparing`, so a
       first draft of this left "Editing Plan B" sitting in the tree after the
       reader had moved to Plan A.

       It does not name the survivor on the way out either, and that is a
       correction: `removeComparison` relabels whichever scenario is kept to slot
       'a', so a reader who was editing Plan B and pressed "keep the plan you are
       editing" was told "Editing Plan A" while looking at B's numbers. The slot is
       not the plan, and the only feedback they get should not say otherwise. */
    setAnnouncement(
      comparing ? 'Now comparing two plans.' : 'Comparison removed. Editing the plan you kept.',
    )
  }, [comparing, activeId])

  /* Roving tabindex: the group is one tab stop and the arrows move within it,
     which is what `radiogroup` promises and what it did not do — there was no
     onKeyDown at all, and both chips were tabbable. Selection follows focus,
     because for a radio group that IS the interaction. */
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!NAV_KEYS.includes(event.key)) return
    event.preventDefault()

    const index = scenarios.findIndex((s) => s.id === activeId)
    if (index === -1) return

    let next = index
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      next = (index - 1 + scenarios.length) % scenarios.length
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      next = (index + 1) % scenarios.length
    } else if (event.key === 'Home') {
      next = 0
    } else {
      next = scenarios.length - 1
    }
    if (next === index) return

    const target = scenarios[next].id
    onFocus(target)
    // Both chips are already mounted, so this needs no effect to land.
    chipRefs.current.get(target)?.focus()
  }

  return (
    <div ref={rootRef} className={comparing ? 'flex items-center gap-2' : 'flex justify-center'}>
      {/* Outside both branches on purpose: a region that unmounts with the thing
          it is describing announces nothing. */}
      <span aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </span>

      {!comparing ? (
        <button
          ref={compareRef}
          type="button"
          onClick={startComparing}
          className="rounded-[var(--radius-full)] border border-[color:var(--c-border)] px-4 text-[length:var(--text-label)] text-[color:var(--c-text-secondary)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out-quart)] hover:bg-[color:var(--c-bg-hover)] hover:text-[color:var(--c-text-primary)]"
          style={{ minHeight: 'var(--touch-target)' }}
        >
          Compare a second plan
        </button>
      ) : (
        <>
          <span id="scenario-label" className="sr-only">
            Scenario being edited
          </span>
          <div
            role="radiogroup"
            aria-labelledby="scenario-label"
            onKeyDown={onKeyDown}
            className="flex flex-1 gap-2"
          >
            {scenarios.map((scenario) => {
              const selected = scenario.id === activeId
              return (
                <button
                  key={scenario.id}
                  ref={(node) => {
                    chipRefs.current.set(scenario.id, node)
                  }}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => onFocus(scenario.id)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-[var(--radius-full)] border text-[length:var(--text-label)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out-quart)]"
                  style={{
                    minHeight: 'var(--touch-target)',
                    borderColor: selected ? 'transparent' : 'var(--c-border)',
                    background: selected ? `var(${scenario.hueToken})` : 'var(--c-bg-surface)',
                    color: selected ? 'var(--c-text-on-accent)' : 'var(--c-text-secondary)',
                  }}
                >
                  {/* On the unselected chip the hue is the only thing tying it to its
                      fan, so it stays visible as a dot rather than disappearing. */}
                  {!selected && (
                    <span
                      aria-hidden="true"
                      className="inline-block size-2 rounded-[var(--radius-full)]"
                      style={{ background: `var(${scenario.hueToken})` }}
                    />
                  )}
                  Plan {scenario.label}
                </button>
              )
            })}
          </div>

          <button
            type="button"
            onClick={stopComparing}
            aria-label="Stop comparing, keep the plan you are editing"
            className="grid shrink-0 place-items-center rounded-[var(--radius-full)] border border-[color:var(--c-border)] text-[color:var(--c-text-tertiary)] transition-colors duration-[var(--dur-fast)] hover:bg-[color:var(--c-bg-hover)] hover:text-[color:var(--c-text-primary)]"
            style={{ width: 'var(--touch-target)', height: 'var(--touch-target)' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </>
      )}
    </div>
  )
}
